import React, { useCallback, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import { Button, DatePicker, Select, Skeleton, Tooltip } from "antd";
import { ArrowDown, ArrowUp, ArrowsClockwise, Minus, Printer, X } from "@phosphor-icons/react";
import dayjs from "dayjs";
import useAuth from "../../hooks/useAuth";
import {
  fetchCaseload,
  fetchConversion,
  fetchCosts,
  fetchOverview,
  fetchScorecard,
  fetchSignups,
  fetchTimeToMatter,
} from "../../service/partnerReports";
import usePartnerFilters, { PRESETS, matchPreset } from "./usePartnerFilters";
import usePanel from "./usePanel";
import DrillDrawer from "./DrillDrawer";
import {
  areaColor,
  conversionMonthlyOption,
  costsMonthlyOption,
  downloadText,
  money,
  pct,
  signed,
  signupsAreaOption,
  sparklineOption,
  timeHistogramOption,
  timeTrendOption,
  toCsv,
  trendSparkOption,
} from "./charts";
import * as S from "./PartnersStyled";

export const PARTNER_DASHBOARD_CAPABILITY = "reports.partner_dashboard";

const { RangePicker } = DatePicker;

const fmtDate = (iso) => (iso ? dayjs(iso).format("D MMM") : "—");
const fmtDateTime = (iso) => (iso ? dayjs(iso).format("D MMM, HH:mm") : "—");
const num = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString());

const DeltaArrow = ({ dir }) => {
  if (dir === "up") return <ArrowUp size={12} weight="bold" />;
  if (dir === "down") return <ArrowDown size={12} weight="bold" />;
  return <Minus size={12} weight="bold" />;
};

const monthName = (iso) => (iso ? dayjs(iso).format("MMM") : "");
const monthYear = (iso) => (iso ? dayjs(iso).format("MMM ’YY") : "");

/**
 * How a scorecard cell compares one month with another. Counts compare by
 * percent; rates by points; days by days. ``goodWhen`` says which direction
 * deserves green ("up", "down" or null for no judgement).
 */
// ``diff`` is the rounded figure that is shown, so the colour never
// disagrees with the text (a "0%" is never green or red).
const compare = (now, then, kind) => {
  if (now === null || now === undefined || then === null || then === undefined) return null;
  if (kind === "rate") {
    const pts = Math.round((now - then) * 100);
    return { text: `${signed(pts)} pts`, diff: pts };
  }
  if (kind === "days") {
    const d = Math.round((now - then) * 10) / 10;
    return { text: `${signed(d)}d`, diff: d };
  }
  if (!then) return now ? { text: `from ${num(then)}`, diff: now - then } : { text: "0 → 0", diff: 0 };
  const pctChange = Math.round(((now - then) / then) * 100);
  return { text: `${signed(pctChange)}%`, diff: pctChange };
};

const toneFor = (cmp, goodWhen) => {
  if (!cmp || !goodWhen || Math.abs(cmp.diff) < 1e-9) return undefined;
  const up = cmp.diff > 0;
  return (goodWhen === "up") === up ? "good" : "bad";
};

/** One cell of the scorecard: last complete month, two comparisons, the trend, this month so far. */
function ScoreCell({ block, months, labels, kind, goodWhen, color, format, soFar, onClick }) {
  const { last_month: lm, series } = block;
  const vsPrev = compare(lm.value, lm.prev, kind);
  const vsYear = compare(lm.value, lm.last_year, kind);
  const fmt = (v) => (v === null || v === undefined ? "—" : format(v));
  return (
    <td className="num" {...(onClick ? { onClick, title: "See the records", style: { cursor: "zoom-in" } } : {})}>
      <S.ScoreTop>
        <S.ScoreValue>{fmt(lm.value)}</S.ScoreValue>
        <S.ScoreSpark>
          <ReactECharts style={{ height: 30, width: 96 }} option={trendSparkOption(series, months, color, fmt)} notMerge />
        </S.ScoreSpark>
      </S.ScoreTop>
      <S.ScoreSub $tone={toneFor(vsPrev, goodWhen)}>{vsPrev ? `${vsPrev.text} vs ${labels.prev}` : `— vs ${labels.prev}`}</S.ScoreSub>
      <S.ScoreSub $tone={toneFor(vsYear, goodWhen)}>{vsYear ? `${vsYear.text} vs ${labels.lastYear}` : `— vs ${labels.lastYear}`}</S.ScoreSub>
      {soFar ? <S.ScoreSub style={{ color: "#98a6ba" }}>{soFar}</S.ScoreSub> : null}
    </td>
  );
}

function PanelFrame({ title, hint, span, children, actions }) {
  return (
    <S.Panel $span={span}>
      <S.PanelHead>
        <div>
          <S.PanelTitle>{title}</S.PanelTitle>
          {hint ? <div><S.PanelHint>{hint}</S.PanelHint></div> : null}
        </div>
        {actions}
      </S.PanelHead>
      {children}
    </S.Panel>
  );
}

function Loading({ rows = 4 }) {
  return <Skeleton active paragraph={{ rows }} title={false} />;
}

export default function Partners() {
  const { user, hasCapability } = useAuth();
  const allowed = hasCapability(PARTNER_DASHBOARD_CAPABILITY);
  const { filters, practiceArea, update, setPracticeArea, toggleAttorney, applyPreset, clear } = usePartnerFilters();
  const [drill, setDrill] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Bumping refreshKey re-runs every panel; the backend cache keys on the
  // mirror's freshness, so this only costs a round-trip.
  const panelFilters = useMemo(() => ({ ...filters, _r: refreshKey }), [filters, refreshKey]);
  const overview = usePanel(fetchOverview, panelFilters, allowed);
  const scorecard = usePanel(fetchScorecard, panelFilters, allowed);
  const signups = usePanel(fetchSignups, panelFilters, allowed);
  const conversion = usePanel(fetchConversion, panelFilters, allowed);
  const timing = usePanel(fetchTimeToMatter, panelFilters, allowed);
  const caseload = usePanel(fetchCaseload, panelFilters, allowed);
  const costs = usePanel(fetchCosts, panelFilters, allowed);

  const openDrill = useCallback((kind, extra = {}, label = "") => setDrill({ kind, extra, label }), []);

  const preset = matchPreset(filters.start, filters.end);
  const attorneyOptions = useMemo(() => overview.data?.options?.attorneys || [], [overview.data]);
  const activeAttorneys = filters.attorneys || [];
  const anyFilter = activeAttorneys.length > 0;
  const accent = areaColor(practiceArea);

  if (user && !allowed) {
    return (
      <S.NoAccess>
        <h2>Partner dashboard</h2>
        <p>This page is for the firm&apos;s partners. If you should have access, ask Matthew to add you.</p>
      </S.NoAccess>
    );
  }

  const fresh = overview.data?.freshness;
  const dataThrough = fresh?.mirror_matters_updated_at || null;
  const cl = caseload.data;
  const co = costs.data;
  const tm = timing.data;
  const cv = conversion.data;
  const su = signups.data;
  const sc = scorecard.data;
  const waitingOver90 = tm?.waiting_now?.histogram?.find((h) => h.from_days === 91)?.count ?? null;
  const snapshotDays = fresh?.snapshot_days || 0;
  const trendReady = cl?.trend_basis === "snapshot";

  const exportCaseloadCsv = () => {
    if (!cl?.attorneys?.length) return;
    const cols = [
      { title: "Attorney", key: "attorney_name" },
      { title: "Open", key: "open" },
      { title: "New (60d)", key: "new_last_60d" },
      { title: "Closed approx (60d)", key: "closed_last_60d" },
      { title: `Open ${cl.trend_window_days}d ago (${cl.trend_basis})`, key: "baseline_60d_ago" },
      { title: "Change", key: "delta" },
    ];
    downloadText(`partners-caseload-${practiceArea.replace(/\s+/g, "-").toLowerCase()}-${cl.as_of}.csv`, toCsv(cl.attorneys, cols));
  };

  return (
    <S.Page>
      <S.Header>
        <S.HeaderRow>
          <div>
            <S.Title>
              Partner dashboard <span style={{ color: "#66758a", fontWeight: 500 }}>·</span>{" "}
              <span style={{ color: accent }}>{practiceArea}</span>
            </S.Title>
            <S.Subtle>
              {fresh
                ? `Case data as of ${fmtDateTime(fresh.mirror_matters_updated_at)} · history ${snapshotDays ? `${snapshotDays} day${snapshotDays === 1 ? "" : "s"} (since ${fmtDate(fresh.snapshot_first_day)})` : "starts tonight"} · ${fresh.timezone}`
                : "Loading…"}
            </S.Subtle>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Tooltip title="Re-fetch every panel">
              <Button size="small" icon={<ArrowsClockwise size={14} />} onClick={() => setRefreshKey((k) => k + 1)}>
                Refresh
              </Button>
            </Tooltip>
            <Tooltip title="Print or save as PDF">
              <Button size="small" icon={<Printer size={14} />} onClick={() => window.print()}>
                Print
              </Button>
            </Tooltip>
          </div>
        </S.HeaderRow>

        <S.FilterBar>
          {PRESETS.map((p) => (
            <S.Chip key={p.key} $active={preset === p.key} onClick={() => applyPreset(p.key)}>
              {p.label}
            </S.Chip>
          ))}
          <RangePicker
            size="small"
            allowClear={false}
            value={[dayjs(filters.start), dayjs(filters.end)]}
            onChange={(vals) => {
              if (!vals || !vals[0] || !vals[1]) return;
              update({ start: vals[0].format("YYYY-MM-DD"), end: vals[1].format("YYYY-MM-DD") });
            }}
            disabledDate={(d) => d && d.isAfter(dayjs().endOf("day"))}
          />
          <span style={{ width: 8 }} />
          <Select
            size="small"
            mode="multiple"
            allowClear
            placeholder="Attorney"
            style={{ minWidth: 180, maxWidth: 360 }}
            value={activeAttorneys}
            onChange={(vals) => update({ attorneys: vals })}
            options={attorneyOptions.map((a) => ({ value: a.attorney_id, label: a.attorney_name }))}
            optionFilterProp="label"
            maxTagCount="responsive"
          />
          {anyFilter && (
            <S.Chip onClick={clear}>
              <X size={12} /> clear
            </S.Chip>
          )}
        </S.FilterBar>
      </S.Header>

      <S.Body>
        {/* ---------------- Scorecard: one row per practice area ---------------- */}
        <S.Scorecard>
          <S.ScoreTable $accent={accent} data-testid="scorecard">
            <thead>
              <tr>
                <th>Practice area</th>
                <th className="num">Sign-ups<small>{sc ? `${monthYear(sc.last_month)} · matters opened` : ""}</small></th>
                <th className="num">Conversion<small>{sc ? `${monthYear(sc.last_month)} intakes · still maturing` : ""}</small></th>
                <th className="num">First contact → matter<small>{sc ? `${monthYear(sc.last_month)} · median days` : ""}</small></th>
                <th className="num">Open caseload<small>{sc ? `end of ${monthYear(sc.last_month)}` : ""}</small></th>
                <th className="num">Prospects waiting<small>{sc ? `end of ${monthYear(sc.last_month)}` : ""}</small></th>
              </tr>
            </thead>
            <tbody>
              {scorecard.loading && !sc ? (
                <tr>
                  <td colSpan={6}><Loading rows={4} /></td>
                </tr>
              ) : sc?.practice_areas?.length ? (
                sc.practice_areas.map((r) => {
                  const selected = r.practice_area === practiceArea;
                  const color = areaColor(r.practice_area);
                  const labels = { prev: monthName(sc.prev_month), lastYear: monthYear(sc.last_year_month) };
                  const months = sc.months;
                  const lastMonthKey = sc.last_month.slice(0, 7);
                  const thisMonth = monthName(sc.months[sc.months.length - 1]);
                  // A click on the selected row's cells opens that month's list;
                  // a click anywhere else selects the row.
                  const drill = (kind, extra, label) => (selected ? (e) => { e.stopPropagation(); openDrill(kind, extra, `${r.practice_area} · ${label}`); } : undefined);
                  const cvNow = r.conversion.this_month;
                  return (
                    <tr key={r.practice_area} className={selected ? "selected" : ""} onClick={() => setPracticeArea(r.practice_area)} title={selected ? "" : `Show ${r.practice_area}`}>
                      <td>
                        <S.Dot $color={color} /> {r.practice_area}
                      </td>
                      <ScoreCell
                        block={r.signups} months={months} labels={labels} kind="count" goodWhen="up" color={color} format={num}
                        soFar={`${thisMonth} so far ${num(r.signups.this_month.value)} · on pace for ≈${num(r.signups.this_month.pace)}`}
                        onClick={drill("signups", { month: lastMonthKey }, `${monthYear(sc.last_month)} sign-ups`)}
                      />
                      <ScoreCell
                        block={r.conversion} months={months} labels={labels} kind="rate" goodWhen="up" color={color} format={(v) => pct(v)}
                        soFar={`${thisMonth} so far ${pct(cvNow.rate)} · ${num(cvNow.converted)} of ${num(cvNow.total)}`}
                        onClick={drill("converted", { month: lastMonthKey }, `${monthYear(sc.last_month)} intakes that signed`)}
                      />
                      <ScoreCell
                        block={r.time_to_matter} months={months} labels={labels} kind="days" goodWhen="down" color={color} format={(v) => `${v}d`}
                        soFar={r.time_to_matter.this_month.count ? `${thisMonth} so far ${r.time_to_matter.this_month.median_days}d · ${num(r.time_to_matter.this_month.count)} matters` : `${thisMonth} so far: no matters opened`}
                        onClick={drill("time_to_matter", { month: lastMonthKey }, `${monthYear(sc.last_month)} matters, days from first contact`)}
                      />
                      <ScoreCell
                        block={r.caseload} months={months} labels={labels} kind="count" goodWhen={null} color={color} format={num}
                        soFar={`now ${num(r.caseload.this_month.open)} · +${num(r.caseload.this_month.new)} new · −${num(r.caseload.this_month.closed)} closed in ${thisMonth}`}
                        onClick={drill("open_caseload", {}, "open matters today")}
                      />
                      <ScoreCell
                        block={r.waiting} months={months} labels={labels} kind="count" goodWhen="down" color={color} format={num}
                        soFar={`now ${num(r.waiting.this_month.count)} · ${num(r.waiting.this_month.over_90_days)} waiting 90+ days`}
                        onClick={drill("waiting", {}, "prospects waiting today, oldest first")}
                      />
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6}><S.Empty>{scorecard.error || "No data"}</S.Empty></td>
                </tr>
              )}
            </tbody>
          </S.ScoreTable>
          <S.ScoreHint>
            Each practice area stands on its own — there is no firm total. The big number is the last complete month, compared with the month before and with the same month last year; the line is the last 12 months (hollow dot = this month, still running); the grey line is this month so far.
            Caseload and waiting history before mid-October 2026 is rebuilt from when records were last edited, so bulk clean-ups in Litify show as steps.
            Click a row to show that practice area below; click a number on the highlighted row to see the records behind it.
          </S.ScoreHint>
        </S.Scorecard>

        {/* ---------------- Sign-ups ---------------- */}
        <PanelFrame
          span={8}
          title={`${practiceArea} sign-ups by month`}
          hint="A sign-up is the day a matter was opened for the intake. Click a bar to see the matters."
        >
          {signups.loading && !su ? (
            <Loading rows={6} />
          ) : su ? (
            <ReactECharts
              style={{ height: 260 }}
              option={signupsAreaOption(su.monthly, practiceArea, dataThrough)}
              onEvents={{
                click: (p) => {
                  if (p?.data?.month) openDrill("signups", { month: p.data.month.slice(0, 7) }, practiceArea);
                },
              }}
              notMerge
            />
          ) : (
            <S.Empty>{signups.error || "No data"}</S.Empty>
          )}
        </PanelFrame>

        <PanelFrame span={4} title="Sign-ups by attorney" hint="In the window. Click a name to filter the page to that attorney.">
          {signups.loading && !su ? (
            <Loading rows={6} />
          ) : su?.by_attorney?.length ? (
            <S.MiniTable>
              <tbody>
                {su.by_attorney.slice(0, 12).map((row) => {
                  const max = su.by_attorney[0]?.count || 1;
                  return (
                    <tr key={row.attorney_id || "none"} className={`clickable${activeAttorneys.includes(row.attorney_id) ? " active" : ""}`} onClick={() => row.attorney_id && toggleAttorney(row.attorney_id)}>
                      <td style={{ width: "45%" }}>{row.attorney_name}</td>
                      <td>
                        <S.BarTrack>
                          <S.Bar $pct={(row.count / max) * 100} $color={accent} />
                        </S.BarTrack>
                      </td>
                      <td className="num" style={{ width: 48 }}>{row.count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </S.MiniTable>
          ) : (
            <S.Empty>No sign-ups in this window</S.Empty>
          )}
        </PanelFrame>

        {/* ---------------- Conversion ---------------- */}
        <PanelFrame
          span={8}
          title={`${practiceArea} conversion by intake month`}
          hint="Of the prospects who first contacted us each month, where they stand today. Recent months are still in motion."
        >
          {conversion.loading && !cv ? (
            <Loading rows={6} />
          ) : cv ? (
            <ReactECharts
              style={{ height: 280 }}
              option={conversionMonthlyOption(cv.monthly, dataThrough)}
              onEvents={{
                click: (p) => p?.data?.month && p?.data?.kind && openDrill(p.data.kind, { month: p.data.month.slice(0, 7) }),
              }}
              notMerge
            />
          ) : (
            <S.Empty>{conversion.error || "No data"}</S.Empty>
          )}
        </PanelFrame>

        <PanelFrame span={4} title="Where the window's prospects landed" hint="Prospects who first contacted us in the window. Click a box to see them.">
          {conversion.loading && !cv ? (
            <Loading rows={6} />
          ) : cv ? (
            <>
              <S.Outcomes>
                <S.Outcome onClick={() => openDrill("converted", {}, "Signed")}>
                  <strong style={{ color: "#1f7a4d" }}>{num(cv.converted)}</strong>
                  <span>signed</span>
                </S.Outcome>
                <S.Outcome onClick={() => openDrill("turned_down", {}, "Turned down")}>
                  <strong style={{ color: "#b42318" }}>{num(cv.turned_down)}</strong>
                  <span>turned down</span>
                </S.Outcome>
                <S.Outcome onClick={() => openDrill("in_progress", {}, "Still in progress")}>
                  <strong style={{ color: "#E07A2F" }}>{num(cv.in_progress)}</strong>
                  <span>still in progress</span>
                </S.Outcome>
                <S.Outcome onClick={() => openDrill("archived", {}, "Archived")}>
                  <strong style={{ color: "#66758a" }}>{num(cv.archived)}</strong>
                  <span>archived{cv.referred_out ? ` · ${num(cv.referred_out)} referred out` : ""}</span>
                </S.Outcome>
              </S.Outcomes>
              {cv.waiting_by_status?.length ? (
                <>
                  <S.PanelHint>Where today&apos;s prospects sit (all of them, not just the window)</S.PanelHint>
                  <S.MiniTable>
                    <tbody>
                      {cv.waiting_by_status.slice(0, 6).map((r) => (
                        <tr key={r.status}>
                          <td>{r.status}</td>
                          <td className="num">{num(r.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </S.MiniTable>
                </>
              ) : null}
            </>
          ) : (
            <S.Empty>{conversion.error || "No intakes in this window"}</S.Empty>
          )}
        </PanelFrame>

        {/* ---------------- Time to matter ---------------- */}
        <PanelFrame span={4} title="First contact → matter opened" hint="How long prospects waited, for matters opened in the window. Click a bar.">
          {timing.loading && !tm ? (
            <Loading rows={6} />
          ) : tm ? (
            <ReactECharts
              style={{ height: 240 }}
              option={timeHistogramOption(tm.histogram, accent)}
              onEvents={{ click: () => openDrill("time_to_matter", {}, "Sorted by days to open in the drawer") }}
              notMerge
            />
          ) : (
            <S.Empty>{timing.error || "No data"}</S.Empty>
          )}
        </PanelFrame>

        <PanelFrame span={4} title="Is it getting faster?" hint="Median and 90th-percentile days from first contact to matter, by month opened.">
          {timing.loading && !tm ? <Loading rows={6} /> : tm ? <ReactECharts style={{ height: 240 }} option={timeTrendOption(tm.monthly, dataThrough)} notMerge /> : <S.Empty>{timing.error || "No data"}</S.Empty>}
        </PanelFrame>

        <PanelFrame span={4} title="Prospects waiting right now" hint="Not yet signed, turned down or archived — by how long they have waited. Click a bar.">
          {timing.loading && !tm ? (
            <Loading rows={6} />
          ) : tm ? (
            <>
              <ReactECharts
                style={{ height: 240 }}
                option={timeHistogramOption(tm.waiting_now.histogram, "#E07A2F")}
                onEvents={{ click: () => openDrill("waiting", {}, "Oldest first") }}
                notMerge
              />
              {waitingOver90 > 0 && (
                <S.Note>
                  {num(waitingOver90)} prospects have waited more than 90 days. Click the bar to see them, oldest first.
                </S.Note>
              )}
            </>
          ) : (
            <S.Empty>{timing.error || "No data"}</S.Empty>
          )}
        </PanelFrame>

        {/* ---------------- Caseload ---------------- */}
        <PanelFrame
          span={8}
          title={`${practiceArea} caseload by attorney`}
          hint={
            cl
              ? trendReady
                ? `Open matters today vs ${cl.trend_window_days} days ago (${fmtDate(cl.trend_baseline_day)}). Up more than 5% is red, down is green. Click a row to see the matters.`
                : `Open matters today. The 60-day arrow switches on once ${cl.trend_window_days} days of nightly history exist (${snapshotDays} so far); until then: new matters in the last 60 days, and closures approximated from last-edit dates. Click a row to see the matters.`
              : ""
          }
          actions={
            <Button size="small" onClick={exportCaseloadCsv} disabled={!cl?.attorneys?.length}>
              CSV
            </Button>
          }
        >
          {caseload.loading && !cl ? (
            <Loading rows={10} />
          ) : cl?.attorneys?.length ? (
            <S.MiniTable>
              <thead>
                <tr>
                  <th>Attorney</th>
                  <th className="num">Open</th>
                  <th style={{ width: "30%" }}>Share</th>
                  <th className="num">{trendReady ? "60-day change" : "New 60d"}</th>
                  {!trendReady && <th className="num">Closed ≈60d</th>}
                  {trendReady && <th style={{ width: 90 }}>90 days</th>}
                </tr>
              </thead>
              <tbody>
                {cl.attorneys.map((a) => {
                  const max = cl.attorneys[0]?.open || 1;
                  return (
                    <tr key={a.attorney_id || "none"} className={`clickable${activeAttorneys.includes(a.attorney_id) ? " active" : ""}`} onClick={() => openDrill("open_caseload", { attorney: a.attorney_id }, a.attorney_name)}>
                      <td>
                        <span
                          role="button"
                          tabIndex={0}
                          style={{ fontWeight: 600 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (a.attorney_id) toggleAttorney(a.attorney_id);
                          }}
                          onKeyDown={(e) => e.key === "Enter" && a.attorney_id && toggleAttorney(a.attorney_id)}
                          title="Filter the page to this attorney"
                        >
                          {a.attorney_name}
                        </span>
                      </td>
                      <td className="num"><strong>{num(a.open)}</strong></td>
                      <td>
                        <S.BarTrack>
                          <S.Bar $pct={(a.open / max) * 100} $color={accent} />
                        </S.BarTrack>
                      </td>
                      {trendReady ? (
                        <>
                          <td className="num">
                            <S.Arrow $dir={a.direction}>
                              <DeltaArrow dir={a.direction} /> {signed(a.delta)}
                            </S.Arrow>
                          </td>
                          <td>
                            {a.sparkline?.length > 1 ? <ReactECharts style={{ height: 24, width: 90 }} option={sparklineOption(a.sparkline, a.direction)} notMerge /> : <S.PanelHint>—</S.PanelHint>}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="num">+{num(a.new_last_60d)}</td>
                          <td className="num" style={{ color: "#7688a3" }}>~{num(a.closed_last_60d)}</td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </S.MiniTable>
          ) : (
            <S.Empty>{caseload.error || `No open ${practiceArea} matters match these filters`}</S.Empty>
          )}
        </PanelFrame>

        {/* ---------------- Costs (Phase 2) ---------------- */}
        <PanelFrame
          span={4}
          title={`${practiceArea} costs expended & recovered`}
          hint="Costs advanced and recovered in the window, plus unpaid balances on open files. Click any box or bar."
        >
          {costs.loading && !co ? (
            <Loading rows={8} />
          ) : co?.has_cost_data ? (
            <>
              <S.Outcomes>
                <S.Outcome onClick={() => openDrill("costs_advanced", {}, "Costs advanced")}>
                  <strong style={{ color: accent }}>{money(co.advanced)}</strong>
                  <span>advanced ({num(co.advanced_count)} items · prior {money(co.prior_advanced)})</span>
                </S.Outcome>
                <S.Outcome onClick={() => openDrill("costs_recovered", {}, "Costs recovered")}>
                  <strong style={{ color: "#1f7a4d" }}>{money(co.recovered)}</strong>
                  <span>recovered ({num(co.recovered_count)} paid{co.recovery_rate ? ` · ${pct(co.recovery_rate)}` : ""})</span>
                </S.Outcome>
                <S.Outcome onClick={() => openDrill("costs_outstanding", {}, "Open balance due")}>
                  <strong style={{ color: "#48566b" }}>{money(co.outstanding_open)}</strong>
                  <span>unpaid on {num(co.outstanding_open_matters)} open files</span>
                </S.Outcome>
                <S.Outcome onClick={() => openDrill("costs_at_risk", {}, "Open 24+ months with balance")}>
                  <strong style={{ color: "#b42318" }}>{money(co.at_risk_24m)}</strong>
                  <span>at risk · {num(co.at_risk_24m_matters)} files open 24+ mo</span>
                </S.Outcome>
              </S.Outcomes>
              <ReactECharts
                style={{ height: 180 }}
                option={costsMonthlyOption(co.monthly, practiceArea, co.data_through || dataThrough)}
                onEvents={{
                  click: (p) => p?.data?.month && p?.data?.kind && openDrill(p.data.kind, { month: p.data.month.slice(0, 7) }, practiceArea),
                }}
                notMerge
              />
              {co.top_matters?.length ? (
                <>
                  <S.PanelHint style={{ marginTop: 8 }}>Top files by cost advanced in the window</S.PanelHint>
                  <S.MiniTable>
                    <tbody>
                      {co.top_matters.slice(0, 5).map((m) => (
                        <tr
                          key={m.matter_id}
                          className="clickable"
                          onClick={() => openDrill("costs_advanced", {}, m.matter_name)}
                        >
                          <td style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>{m.matter_name}</td>
                          <td className="num"><strong>{money(m.advanced)}</strong></td>
                          <td className="num" style={{ color: "#7688a3" }}>{m.due > 0 ? `${money(m.due)} due` : "paid"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </S.MiniTable>
                </>
              ) : null}
            </>
          ) : (
            <S.Empty>
              {costs.error || "No cost records synced yet — the nightly snapshot will populate this panel."}
            </S.Empty>
          )}
        </PanelFrame>
      </S.Body>

      <DrillDrawer request={drill} filters={filters} onClose={() => setDrill(null)} />
    </S.Page>
  );
}
